import React, { useState, useMemo, useEffect } from 'react';
import Page from '../../layout/containers/Page/Page';
import pageName from './page';
import { usePricelists } from './hooks/usePricelists';
import PageHeader from './components/PageHeader';
import PricelistCard from './components/PricelistCard';
import AddTyreModal from './components/AddTyreModal';
import EditTyreModal from './components/EditTyreModal';
import XlsxImportModal from './components/XlsxImportModal';
import {
  Container,
  PricelistsGrid,
  LoadingSpinner,
  EmptyState,
  ErrorBox,
  Button,
} from './PriceListsPage.style';
import { useTranslation } from 'react-i18next';

const initialFormData = {
  pricelist: '',
  pricelistLabel: '',
  dimension: '',
  model: '',
  bruto: '',
  neto: '',
  brutoZPopustom: '',
};

const PriceListsPage = () => {
  // View state
  const [viewMode, setViewMode] = useState('season');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [editingPrice, setEditingPrice] = useState(null);
  const { t } = useTranslation();

  // Data from hook
  const {
    pricelists,
    brands,
    seasons,
    loading,
    error,
    refetch,
    handlePriceEdit,
    saveTyrePrice,
    handleDeleteTyre,
    addTyre,
    editTyre,
    uploadPricelist,
  } = usePricelists();

  // Set initial selections when data loads
  useEffect(() => {
    if (brands.length > 0 && !selectedBrand) {
      setSelectedBrand(brands[0]);
    }
    if (seasons.length > 0 && !selectedSeason) {
      setSelectedSeason(seasons[0]);
    }
  }, [brands, seasons, selectedBrand, selectedSeason]);

  // Add modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addFormData, setAddFormData] = useState(initialFormData);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ ...initialFormData, tyreId: '' });

  // XLSX import modal
  const [xlsxModalOpen, setXlsxModalOpen] = useState(false);
  const [xlsxData, setXlsxData] = useState({
    file: null,
    fileName: '',
    uploading: false,
    error: null,
    success: false,
  });

  // Computed
  const visiblePricelists = useMemo(() => {
    if (viewMode === 'season') {
      return brands.map((brand) => pricelists[`${brand}-${selectedSeason}`]).filter(Boolean);
    }
    return seasons.map((seasonKey) => pricelists[`${selectedBrand}-${seasonKey}`]).filter(Boolean);
  }, [pricelists, brands, seasons, viewMode, selectedSeason, selectedBrand]);

  // Add modal handlers
  const openAddModal = (pricelistKey, pricelist) => {
    setAddFormData({
      pricelist: pricelistKey,
      pricelistLabel: `${pricelist.brand} - ${pricelist.seasonLabel}`,
      dimension: '',
      model: '',
      bruto: '',
      neto: '',
      brutoZPopustom: '',
    });
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setAddFormData(initialFormData);
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    addTyre(addFormData.pricelist, {
      dimension: addFormData.dimension,
      model: addFormData.model,
      bruto: addFormData.bruto,
      neto: addFormData.neto,
      brutoZPopustom: addFormData.brutoZPopustom,
    });
    closeAddModal();
  };

  // Edit modal handlers
  const openEditModal = (pricelistKey, tyre) => {
    const pricelist = pricelists[pricelistKey];
    setEditFormData({
      pricelist: pricelistKey,
      pricelistLabel: `${pricelist.brand} - ${pricelist.seasonLabel}`,
      tyreId: tyre.id,
      dimension: tyre.dimension,
      model: tyre.model,
      bruto: (tyre.bruto || 0).toString(),
      neto: (tyre.neto || 0).toString(),
      brutoZPopustom: (tyre.brutoZPopustom || 0).toString(),
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditFormData({ ...initialFormData, tyreId: '' });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    editTyre(editFormData.pricelist, {
      id: editFormData.tyreId,
      dimension: editFormData.dimension,
      model: editFormData.model,
      bruto: editFormData.bruto,
      neto: editFormData.neto,
      brutoZPopustom: editFormData.brutoZPopustom,
    });
    closeEditModal();
  };

  // XLSX handlers
  const openXlsxModal = () => setXlsxModalOpen(true);

  const closeXlsxModal = () => {
    setXlsxModalOpen(false);
    setXlsxData({
      file: null,
      fileName: '',
      uploading: false,
      error: null,
      success: false,
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setXlsxData({
        file,
        fileName: file.name,
        uploading: false,
        error: null,
        success: false,
      });
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (file && (validTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setXlsxData({
        file,
        fileName: file.name,
        uploading: false,
        error: null,
        success: false,
      });
    } else {
      setXlsxData((prev) => ({
        ...prev,
        error: t('pleaseUploadXlsxOnly'),
      }));
    }
  };

  const handleXlsxImport = async () => {
    if (xlsxData.success || !xlsxData.file) return;
    
    setXlsxData((prev) => ({ ...prev, uploading: true, error: null }));
    
    const result = await uploadPricelist(xlsxData.file);
    
    if (result.success) {
      setXlsxData((prev) => ({ ...prev, uploading: false, success: true }));
      setTimeout(closeXlsxModal, 2000);
    } else {
      setXlsxData((prev) => ({ ...prev, uploading: false, error: result.error }));
    }
  };

  return (
    <Page page={pageName}>
      <Container>
        <PageHeader
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedSeason={selectedSeason}
          onSeasonChange={setSelectedSeason}
          selectedBrand={selectedBrand}
          onBrandChange={setSelectedBrand}
          brands={brands}
          seasons={seasons}
          onImportClick={openXlsxModal}
        />

        {loading && (
          <LoadingSpinner>
            <i className="mdi mdi-loading mdi-spin" />
            <span>{t('loadingPriceLists')}</span>
          </LoadingSpinner>
        )}

        {error && !loading && (
          <ErrorBox>
            <i className="mdi mdi-alert-circle" />
            <div>
              <p>{error}</p>
              <Button onClick={refetch} style={{ marginTop: '12px' }}>
                <i className="mdi mdi-refresh" />
                {t('tryAgain')}
              </Button>
            </div>
          </ErrorBox>
        )}

        {!loading && !error && visiblePricelists.length === 0 && (
          <EmptyState>
            <i className="mdi mdi-car-tire-alert" />
            <p>{t('noPriceListsFound')}</p>
          </EmptyState>
        )}

        {!loading && !error && visiblePricelists.length > 0 && (
          <PricelistsGrid>
            {visiblePricelists.map((pricelist) => {
              const key = `${pricelist.brand}-${pricelist.season}`;
              return (
                <PricelistCard
                  key={key}
                  pricelist={pricelist}
                  pricelistKey={key}
                  onAddTyre={openAddModal}
                  onPriceEdit={handlePriceEdit}
                  onSavePrice={saveTyrePrice}
                  onEditTyre={openEditModal}
                  onDeleteTyre={handleDeleteTyre}
                  editingPrice={editingPrice}
                  onEditingPriceChange={setEditingPrice}
                />
              );
            })}
          </PricelistsGrid>
        )}

        <AddTyreModal
          isOpen={addModalOpen}
          onClose={closeAddModal}
          onSubmit={handleAddSubmit}
          formData={addFormData}
          onFormChange={(field, value) => setAddFormData((prev) => ({ ...prev, [field]: value }))}
        />

        <EditTyreModal
          isOpen={editModalOpen}
          onClose={closeEditModal}
          onSubmit={handleEditSubmit}
          formData={editFormData}
          onFormChange={(field, value) => setEditFormData((prev) => ({ ...prev, [field]: value }))}
        />

        <XlsxImportModal
          isOpen={xlsxModalOpen}
          onClose={closeXlsxModal}
          data={xlsxData}
          onFileSelect={handleFileSelect}
          onFileDrop={handleFileDrop}
          onConfirmImport={handleXlsxImport}
        />
      </Container>
    </Page>
  );
};

export default PriceListsPage;
